import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Item {
  id: string;
  section: string;
  title: string;
  done: boolean;
  doneBy?: string;
  doneAt?: string;
}

interface Today {
  items: Item[];
  total: number;
  done: number;
  outstanding: number;
  readyToClose: boolean;
}

const SECTIONS: Array<[string, string]> = [
  ['production', 'The back'],
  ['front', 'Front of shop'],
];

/**
 * The daily cleaning checklist. Nothing here is optional — the till will not
 * close until every line is signed off.
 */
export default function CleaningPanel({
  branchId,
  branchName,
  isManager,
}: {
  branchId: string;
  branchName?: string;
  isManager: boolean;
}) {
  const [today, setToday] = useState<Today | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newSection, setNewSection] = useState('production');
  const [showAdd, setShowAdd] = useState(false);

  const flash = (m: string) => {
    setMsg(m);
    setErr('');
    setTimeout(() => setMsg(''), 3500);
  };
  const fail = (e: any, fallback: string) => {
    const d = e?.response?.data?.message;
    setErr(Array.isArray(d) ? d.join(', ') : d || fallback);
    setMsg('');
  };

  const load = async () => {
    if (!branchId) return;
    try {
      const res = await apiClient.cleaningToday(branchId);
      setToday(res.data);
    } catch (e: any) {
      fail(e, 'Could not load the checklist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const toggle = async (item: Item) => {
    setBusy(true);
    try {
      const res = await apiClient.setCleaningChecked(item.id, branchId, !item.done);
      setToday(res.data);
      setErr('');
    } catch (e: any) {
      fail(e, 'Could not record that');
    } finally {
      setBusy(false);
    }
  };

  const seed = async () => {
    setBusy(true);
    try {
      const res = await apiClient.seedCleaningList(branchId);
      flash(res.data.message);
      await load();
    } catch (e: any) {
      fail(e, 'Could not set up the list');
    } finally {
      setBusy(false);
    }
  };

  const addItem = async () => {
    if (!newTitle.trim()) return;
    setBusy(true);
    try {
      const res = await apiClient.addCleaningItem(branchId, newSection, newTitle.trim());
      flash(res.data.message);
      setNewTitle('');
      await load();
    } catch (e: any) {
      fail(e, 'Could not add that');
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (item: Item) => {
    if (!confirm(`Take "${item.title}" off the list?`)) return;
    setBusy(true);
    try {
      const res = await apiClient.setCleaningItemActive(item.id, false);
      flash(res.data.message);
      await load();
    } catch (e: any) {
      fail(e, 'Could not change the list');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading…</div>;
  if (!today) return null;

  if (today.total === 0) {
    return (
      <div className="space-y-6">
        {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}
        <div className="bg-white rounded-lg shadow p-8 text-center max-w-lg mx-auto">
          <h3 className="text-lg font-medium text-gray-900">No cleaning list yet</h3>
          <p className="text-sm text-gray-600 mt-2">
            {branchName} has no checklist, so nothing is being enforced at close.
            Start from a standard bakery list and edit it to match how you work.
          </p>
          {isManager ? (
            <button
              onClick={seed}
              disabled={busy}
              className="mt-5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-lg"
            >
              Set up the checklist
            </button>
          ) : (
            <p className="mt-4 text-sm text-gray-500">A manager sets this up.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div
        className={`rounded-lg px-5 py-4 flex items-center justify-between gap-4 flex-wrap ${
          today.readyToClose
            ? 'bg-green-50 border border-green-200'
            : 'bg-amber-50 border border-amber-200'
        }`}
      >
        <div>
          <p
            className={`text-lg font-bold ${
              today.readyToClose ? 'text-green-900' : 'text-amber-900'
            }`}
          >
            {today.readyToClose
              ? 'All signed off — the till can close'
              : `${today.outstanding} still to do before the till can close`}
          </p>
          <p className={`text-sm ${today.readyToClose ? 'text-green-800' : 'text-amber-800'}`}>
            {today.done} of {today.total} done today{branchName ? ` at ${branchName}` : ''}
          </p>
        </div>
        <div className="h-2 w-40 bg-white rounded-full overflow-hidden shrink-0">
          <div
            className={`h-full ${today.readyToClose ? 'bg-green-500' : 'bg-amber-500'}`}
            style={{ width: `${today.total ? (today.done / today.total) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {SECTIONS.map(([key, label]) => {
          const items = today.items.filter((i) => i.section === key);
          if (items.length === 0) return null;
          return (
            <div key={key} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-5 py-3 border-b bg-gray-50">
                <h3 className="font-medium text-gray-900">{label}</h3>
              </div>
              <ul className="divide-y">
                {items.map((i) => (
                  <li key={i.id} className="flex items-center gap-3 px-5 py-3">
                    <input
                      type="checkbox"
                      checked={i.done}
                      onChange={() => toggle(i)}
                      disabled={busy || !isManager}
                      className="w-5 h-5 shrink-0 accent-green-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${i.done ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {i.title}
                      </p>
                      {i.done && i.doneBy && (
                        <p className="text-xs text-gray-500">
                          {i.doneBy}
                          {i.doneAt
                            ? ` · ${new Date(i.doneAt).toLocaleTimeString('en-ZA', { timeStyle: 'short' })}`
                            : ''}
                        </p>
                      )}
                    </div>
                    {isManager && (
                      <button
                        onClick={() => removeItem(i)}
                        className="text-xs text-gray-400 hover:text-red-600 shrink-0"
                        title="Take off the list"
                      >
                        remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {isManager && (
        <div className="bg-white rounded-lg shadow p-5">
          {showAdd ? (
            <div className="flex gap-3 flex-wrap items-center">
              <select
                value={newSection}
                onChange={(e) => setNewSection(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="production">The back</option>
                <option value="front">Front of shop</option>
              </select>
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addItem()}
                placeholder="What needs cleaning?"
                className="flex-1 min-w-48 px-4 py-2 border border-gray-300 rounded-lg"
              />
              <button
                onClick={addItem}
                disabled={busy}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-4 py-2 rounded-lg"
              >
                Add
              </button>
              <button onClick={() => setShowAdd(false)} className="text-sm text-gray-500 hover:underline">
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setShowAdd(true)} className="text-sm text-amber-600 hover:underline font-medium">
              + Add something to the list
            </button>
          )}
        </div>
      )}
    </div>
  );
}
